import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import googleMapsIcon from '../assets/google-maps-icon.png';
import sodaTrackLogo from '../assets/soda-track-logo.png';
import FirebaseUIAuth from '@/components/FirebaseUIAuth';
import { UserCredential } from 'firebase/auth';
import { testApi } from '@/lib/api';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { useAppSelector } from '@/store/hooks';
import {
  selectIsAuthenticated,
  selectAuthLoaded,
  selectUserProfileDetails,
  selectIsAuthLoading,
} from '@/store/slices/authSlice';

const OpeningPage = () => {
  const navigate = useNavigate();
  
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isAuthStoreLoaded = useAppSelector(selectAuthLoaded);
  const userProfileDetails = useAppSelector(selectUserProfileDetails);
  const hasUsername = !!userProfileDetails?.username;
  const isAuthStoreLoading = useAppSelector(selectIsAuthLoading);
  
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthStoreLoaded && !isAuthStoreLoading && isAuthenticated && !hasUsername) {
      console.log('[OpeningPage] Redirecting to create-username page');
      navigate('/create-username');
    }
  }, [isAuthenticated, hasUsername, isAuthStoreLoading, isAuthStoreLoaded, navigate]);

  const handleMapClick = () => {
    navigate('/map');
  };

  const handleLoginSuccess = (authResult: UserCredential) => {
    console.log('Login successful via FirebaseUI:', authResult.user?.displayName || authResult.user?.email);
    // State changes driven by Redux store will handle UI transitions
  };

  const handleTestApiClick = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const result = await testApi();
      console.log('Test API Success:', result);
      setTestResult(`Success: ${JSON.stringify(result)}`);
    } catch (error: any) {
      console.error('Test API Error:', error);
      setTestResult(`Error: ${error.message || 'Unknown error'}`);
    } finally {
      setTestLoading(false);
    }
  };

  // State 1: Initial App Load spinner (very first load before auth state is known)
  if (isAuthStoreLoading && !isAuthStoreLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent"></div>
        <span className="ml-3 text-foreground">Loading App...</span>
      </div>
    );
  }

  // Derived states for different UI phases
  const shouldShowLoginUI = !isAuthenticated && isAuthStoreLoaded && !isAuthStoreLoading;
  const loginIsProcessing = !isAuthenticated && isAuthStoreLoaded && isAuthStoreLoading;
  const postLoginProcessing = isAuthenticated && isAuthStoreLoaded && (isAuthStoreLoading || !hasUsername);
  const shouldShowAuthContent = isAuthenticated && isAuthStoreLoaded && !isAuthStoreLoading && hasUsername;

  return (
    <div className="flex flex-col items-center justify-center gap-8 p-4 min-h-full relative">
      <AnimatePresence mode="wait">
        {/* Case 1: Showing Login Form */}
        {shouldShowLoginUI && (
          <motion.div
            key="login-form-section"
            className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
          >
            <img 
              src={sodaTrackLogo} 
              alt="Soda Track Logo" 
              className="w-auto h-130 md:h-130 mb-4 md:mb-0"
            />
            <Card className="w-full max-w-[350px] text-center shadow-lg bg-card/80 backdrop-blur-sm">
              <CardContent className="flex flex-col items-center gap-6 pt-6">
                <h2 className="text-xl font-semibold">Sign In / Sign Up</h2>
                <FirebaseUIAuth onSuccess={handleLoginSuccess} />
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Case 2: Login is Processing OR Post-Login Processing (e.g. loading profile/redirecting) */}
        {(loginIsProcessing || postLoginProcessing) && (
          <motion.div
            key="loading-spinner-section"
            className="flex flex-col items-center justify-center h-full py-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="animate-spin h-12 w-12 border-4 border-primary rounded-full border-t-transparent"></div>
            <span className="mt-4 text-lg text-muted-foreground">
              {loginIsProcessing ? 'Signing in...' : postLoginProcessing && !hasUsername && isAuthenticated ? 'Finalizing setup...' : 'Loading your experience...'}
            </span>
          </motion.div>
        )}

        {/* Case 3: Showing Authenticated Content */}
        {shouldShowAuthContent && (
          <motion.div
            key="authenticated-content-section"
            className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <img 
              src={sodaTrackLogo} 
              alt="Soda Track Logo" 
              className="w-auto h-130 md:h-130 mb-4 md:mb-0"
            />
            <Card className="w-full max-w-[350px] text-center shadow-lg bg-card/80 backdrop-blur-sm">
              <CardContent className="flex flex-col items-center gap-6 pt-6">
                <div className="flex gap-8 mt-4">
                  <div className="flex flex-col items-center">
                    <Button 
                      variant="outline"
                      size="icon" 
                      className="h-28 w-28 rounded-full mb-2" 
                      onClick={handleMapClick}
                    >
                      <img 
                        src={googleMapsIcon} 
                        alt="Google Maps" 
                        className="h-16 w-16" 
                      />
                      <span className="sr-only">Go to map</span>
                    </Button>
                    <p className="text-sm font-medium text-muted-foreground">Places and<br />Sightings</p>
                  </div>

                 <PermissionGuard requiredPermission="manager_view manage_flavors manage_attributes manage_users">
                  <div className="flex flex-col items-center">
                    <Button 
                      variant="outline"
                      size="icon" 
                      className="h-28 w-28 rounded-full mb-2" 
                      onClick={() => navigate('/manage')}
                    >
                      <span className="text-4xl">🥤</span>
                      <span className="sr-only">Admin</span>
                    </Button>
                    <p className="text-sm font-medium text-muted-foreground">Admin</p>
                  </div>
                  </PermissionGuard>
                </div>
                <PermissionGuard requiredPermission="admin">
                  <div className="mt-4 flex flex-col items-center gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleTestApiClick}
                      disabled={testLoading}
                    >
                      {testLoading ? 'Testing...' : 'Test API'}
                    </Button>
                    {testResult && (
                      <p className="text-xs text-muted-foreground max-w-full break-words px-2">
                        {testResult}
                      </p>
                    )}
                  </div>
                </PermissionGuard>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OpeningPage;
